import type { DateRequestSimilarEvent, Pricings } from './types/event'
import { useFetchPricings } from './useEventPricings'

interface UseDateRequestPricingsOptions {
  quantity: Ref<number>
  isPrivate: Ref<boolean>
}

/**
 * Composable for fetching pricings for multiple date request events
 * Used in the date request product list to show accurate pricing
 */
export const useDateRequestPricings = (
  items: Ref<DateRequestSimilarEvent[]>,
  { quantity, isPrivate }: UseDateRequestPricingsOptions
) => {
  // Map of event ID to its pricing data
  const pricingsMap = ref<Record<string, Pricings>>({})
  const loading = ref(false)
  const errors = ref<Record<string, string>>({})

  /**
   * Fetches pricings for all items in parallel
   */
  const fetchAllPricings = async () => {
    if (!items.value.length) return

    loading.value = true
    errors.value = {}

    try {
      // Fetch all pricings in parallel
      const pricingPromises = items.value.map(async (item) => {
        try {
          const eventId = ref(item.id)
          const dateId = ref(item.bookable_date?.data?.id)

          const pricingData = await useFetchPricings(eventId, {
            dateId,
            quantity,
            isPrivate,
          })

          return {
            itemId: item.id,
            pricing: pricingData,
          }
        } catch (error) {
          console.error(`Failed to fetch pricing for event ${item.id}:`, error)
          errors.value[item.id] =
            error instanceof Error ? error.message : 'Unknown error'
          return {
            itemId: item.id,
            pricing: null,
          }
        }
      })

      const results = await Promise.all(pricingPromises)

      // Store successful results in the map
      const newPricingsMap: Record<string, Pricings> = {}
      results.forEach(({ itemId, pricing }) => {
        if (pricing) {
          newPricingsMap[itemId] = pricing
        }
      })

      pricingsMap.value = newPricingsMap
    } finally {
      loading.value = false
    }
  }

  /**
   * Gets pricing for a specific item
   */
  const getPricingForItem = (itemId: string): Pricings | undefined => {
    return pricingsMap.value[itemId]
  }

  /**
   * Checks if pricing exists for an item
   */
  const hasPricingForItem = (itemId: string): boolean => {
    return !!pricingsMap.value[itemId]
  }

  return {
    pricingsMap: readonly(pricingsMap),
    loading: readonly(loading),
    errors: readonly(errors),
    fetchAllPricings,
    getPricingForItem,
    hasPricingForItem,
  }
}
